/**
 * Calendar Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import type {
  ComponentAlignment,
  LimitInput,
  LimitSettings,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseLimitFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatLimitFormat,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
  formatComponentAlignmentFormat,
  formatResponsiveCornerRadiusFormat,
} from '../../format-utils'
import calendarTemplate from './calendar.template.json'

export interface ICalendarComponentSettings {
  // Data settings
  'calendar-data-source': string
  'calendar-data-title': string
  'calendar-data-start-date': string
  'calendar-data-end-date': string
  'calendar-data-sort-type': string
  'calendar-data-set-limit': LimitSettings

  // Design settings
  'calendar-design-comp-h-align': ResponsiveValue<ComponentAlignment>
  'calendar-design-typography': {
    'calendar-design-title-typography': ResponsiveValue<TypographyValue>
    'calendar-design-content-typography': ResponsiveValue<TypographyValue>
  }
  'calendar-design-background-color': string
  'calendar-design-filter-bg-color': string
  'calendar-design-filter-chip-color': string
  'calendar-design-corner-radius': ResponsiveValue<string>
  'calendar-design-filter-corner-radius': string
  'calendar-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'calendar-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'calendar-design-padding': ResponsiveValue<SpacingInputValue>
  'calendar-design-elevation': boolean

  // Actions
  'calendar-action-on-click': BuilderActions
  'calendar-action-add-event': BuilderActions

  // Behavior settings
  'calendar-behavior-auto-fit-day': boolean
  'calendar-behavior-default-view': string
}

export interface ICalendarComponent extends IBuilderComponent {
  componentId: 'calendar'
  groupId: 'app'
  settings: ICalendarComponentSettings
}

/**
 * Calendar AI Input
 */
export interface AiCalendarInput {
  dataSource: string // Table ID
  titleColumn?: string // Column ID for event title
  startDateColumn?: string // Column ID for start date
  endDateColumn?: string // Column ID for end date
  sortType?: 'asc' | 'desc' // Sort direction
  limit?: LimitInput // Limit format
  width?: string // Format: "w:100%"
  alignment?: string // Format: "parent" or "center"
  margin?: string // Format: "m:24px"
  padding?: string // Format: "p:0px"
  backgroundColor?: string // Background color
  filterBgColor?: string // Filter background color
  filterChipColor?: string // Filter chip color
  cornerRadius?: string // Format: "none", "small", "medium", "large"
  filterCornerRadius?: string // Filter corner radius
  elevation?: boolean // Shadow elevation
  typography?: {
    title?: string // Title typography
    content?: string // Content typography
  }
  autoFitDay?: boolean // Auto fit day view
  defaultView?: string // Default view: "month", "week", "day"
  actionOnClick?: BuilderActions
  actionAddEvent?: BuilderActions
}

const CALENDAR_DEFAULT_TEMPLATE = calendarTemplate as unknown as ICalendarComponentSettings

export class CalendarComp {
  getTemplate(): ICalendarComponentSettings {
    return CALENDAR_DEFAULT_TEMPLATE
  }

  /**
   * format calendar component settings to AI calendar input
   */
  format(settings: ICalendarComponentSettings): AiCalendarInput {
    const result: AiCalendarInput = {
      dataSource: '',
    }

    // Data - Source
    if (settings['calendar-data-source']) {
      result.dataSource = settings['calendar-data-source']
    }

    // Data - Columns
    if (settings['calendar-data-title']) {
      result.titleColumn = settings['calendar-data-title']
    }

    if (settings['calendar-data-start-date']) {
      result.startDateColumn = settings['calendar-data-start-date']
    }

    if (settings['calendar-data-end-date']) {
      result.endDateColumn = settings['calendar-data-end-date']
    }

    // Data - Sort
    if (settings['calendar-data-sort-type']) {
      result.sortType = settings['calendar-data-sort-type'] as 'asc' | 'desc'
    }

    // Data - Limit
    if (settings['calendar-data-set-limit']) {
      result.limit = formatLimitFormat(settings['calendar-data-set-limit'])
    }

    // Design - Width (handle OverflowSettings)
    if (settings['calendar-design-width']) {
      const width = formatWidthFormat(settings['calendar-design-width'])
      if (width) {
        result.width = width
      }
    }

    // Design - Alignment
    if (settings['calendar-design-comp-h-align']) {
      result.alignment = formatComponentAlignmentFormat(settings['calendar-design-comp-h-align'])?.replace(/^h:/, '') || undefined
    }

    // Design - Margin (handle OverflowSettings)
    if (settings['calendar-design-margin']) {
      const margin = formatSpacingFormat(settings['calendar-design-margin'])
      if (margin) {
        result.margin = margin
      }
    }

    // Design - Padding
    if (settings['calendar-design-padding']) {
      result.padding = formatSpacingFormat(settings['calendar-design-padding'])
    }

    // Design - Background Color
    if (settings['calendar-design-background-color']) {
      result.backgroundColor = settings['calendar-design-background-color']
    }

    // Design - Filter Colors
    if (settings['calendar-design-filter-bg-color']) {
      result.filterBgColor = settings['calendar-design-filter-bg-color']
    }

    if (settings['calendar-design-filter-chip-color']) {
      result.filterChipColor = settings['calendar-design-filter-chip-color']
    }

    // Design - Corner Radius
    if (settings['calendar-design-corner-radius']) {
      result.cornerRadius = formatResponsiveCornerRadiusFormat(settings['calendar-design-corner-radius'])
    }

    if (settings['calendar-design-filter-corner-radius']) {
      result.filterCornerRadius = settings['calendar-design-filter-corner-radius']
    }

    // Design - Elevation
    if (settings['calendar-design-elevation'] !== undefined) {
      result.elevation = settings['calendar-design-elevation']
    }

    // Design - Typography
    if (settings['calendar-design-typography']) {
      result.typography = {}
      if (settings['calendar-design-typography']['calendar-design-title-typography']) {
        result.typography.title = formatTypographyFormat(settings['calendar-design-typography']['calendar-design-title-typography'])
      }
      if (settings['calendar-design-typography']['calendar-design-content-typography']) {
        result.typography.content = formatTypographyFormat(settings['calendar-design-typography']['calendar-design-content-typography'])
      }
    }

    // Behavior
    if (settings['calendar-behavior-auto-fit-day'] !== undefined) {
      result.autoFitDay = settings['calendar-behavior-auto-fit-day']
    }

    if (settings['calendar-behavior-default-view']) {
      result.defaultView = settings['calendar-behavior-default-view']
    }

    return result
  }

  /**
   * parse AI calendar input to calendar component settings
   */
  parse(input: AiCalendarInput): ICalendarComponentSettings {
    const result = deepMerge(CALENDAR_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['calendar-data-source'] = input.dataSource
    }

    // Data - Columns
    if (input.titleColumn) {
      result['calendar-data-title'] = input.titleColumn
    }

    if (input.startDateColumn) {
      result['calendar-data-start-date'] = input.startDateColumn
    }

    if (input.endDateColumn) {
      result['calendar-data-end-date'] = input.endDateColumn
    }

    // Data - Sort
    if (input.sortType) {
      result['calendar-data-sort-type'] = input.sortType
    }

    // Data - Limit
    if (input.limit !== undefined) {
      const parsedLimit = parseLimitFormat(input.limit)
      if (parsedLimit) {
        result['calendar-data-set-limit'] = parsedLimit
      }
    }

    // Design - Width
    if (input.width) {
      result['calendar-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['calendar-design-comp-h-align'] = alignmentParsed.hAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['calendar-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Padding
    if (input.padding) {
      result['calendar-design-padding'] = parseSpacingFormat(input.padding)
    }

    // Design - Background Color
    if (input.backgroundColor) {
      result['calendar-design-background-color'] = input.backgroundColor
    }

    // Design - Filter Colors
    if (input.filterBgColor) {
      result['calendar-design-filter-bg-color'] = input.filterBgColor
    }

    if (input.filterChipColor) {
      result['calendar-design-filter-chip-color'] = input.filterChipColor
    }

    // Design - Corner Radius
    if (input.cornerRadius) {
      const radiusMap: Record<string, string> = {
        none: 'none',
        small: 'small',
        medium: 'medium',
        large: 'large',
      }
      const radius = radiusMap[input.cornerRadius] || 'none'
      result['calendar-design-corner-radius'] = {
        phone: radius,
        'phone-landscape': radius,
        tablet: radius,
        'tablet-landscape': radius,
        desktop: radius,
        tv: radius,
      }
    }

    if (input.filterCornerRadius) {
      result['calendar-design-filter-corner-radius'] = input.filterCornerRadius
    }

    // Design - Elevation
    if (input.elevation !== undefined) {
      result['calendar-design-elevation'] = input.elevation
    }

    // Design - Typography
    if (input.typography) {
      if (input.typography.title) {
        const titleTypo = parseTypographyFormat(input.typography.title)
        result['calendar-design-typography']['calendar-design-title-typography'] = titleTypo
      }

      if (input.typography.content) {
        const contentTypo = parseTypographyFormat(input.typography.content)
        result['calendar-design-typography']['calendar-design-content-typography'] = contentTypo
      }
    }

    // Behavior
    if (input.autoFitDay !== undefined) {
      result['calendar-behavior-auto-fit-day'] = input.autoFitDay
    }

    if (input.defaultView) {
      result['calendar-behavior-default-view'] = input.defaultView
    }

    // Actions
    if (input.actionOnClick) {
      result['calendar-action-on-click'] = input.actionOnClick
    }

    if (input.actionAddEvent) {
      result['calendar-action-add-event'] = input.actionAddEvent
    }

    return result
  }
}

